"use client"

import type React from "react"
import { useCallback, useEffect, useState } from "react"
import { FiCopy } from "react-icons/fi"
import { useNavigate, useParams } from "react-router-dom"
import { ToastContainer, toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import { api } from "../services/api"
import CountdownTimer from "./CountdownTimer"
import "./ServicePaymentPage.css"

// Ethereum provider type definition that matches CowSwap's requirements
interface EthereumProvider {
  request: (args: { method: string; params?: any[] }) => Promise<any>
  on: (event: string, handler: (...args: any[]) => void) => void
  removeListener: (event: string, handler: (...args: any[]) => void) => void
  enable: () => Promise<string[]> // Required by CowSwap
  isMetaMask?: boolean
  isConnected?: () => boolean
}

interface PaymentData {
  id: string
  reference: string
  email?: string
  network: string
  token: {
    symbol: string
    name: string
    address: string
    logo_url?: string
  }
  serviceType: string
  carrier: string
  product: string
  productPin?: string
  productInfo?: string
  onchainAmount: number
  offchainAmount: number
  depositAddress?: string
  refundAddress?: string
  expiration?: string
  status: string
  statusMessage?: string
  txHash?: string
  txHashRefund?: string
  unsignedTx?: any // Add this field for HyperCore transactions
}

const formatCarrierName = (carrier: string): string => {
  return carrier.charAt(0).toUpperCase() + carrier.slice(1).toLowerCase()
}

const formatGiftCardInfo = (productPin: string) => {
  if (!productPin) return null

  // Check if it contains both TARJETA and PIN
  if (productPin.includes("TARJETA:") && productPin.includes("_PIN:")) {
    const parts = productPin.split("_PIN:")
    const cardNumber = parts[0].replace("TARJETA:", "")
    const pin = parts[1]

    return {
      cardNumber,
      pin,
      hasCard: true,
    }
  }

  // Check if it's just a PIN
  if (productPin.includes("PIN:")) {
    const pin = productPin.replace("PIN:", "")
    return {
      pin,
      hasCard: false,
    }
  }

  // Fallback - treat the whole string as PIN
  return {
    pin: productPin,
    hasCard: false,
  }
}

function getProductMessage(paymentData: PaymentData): string {
  const { serviceType, reference, offchainAmount } = paymentData
  const formattedCarrier = formatCarrierName(paymentData.carrier)

  return `Compra de ${serviceType} - ${formattedCarrier} de $${offchainAmount} MXN para número móvil ${reference}.`
}

const ServicePaymentPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isProcessingTransaction, setIsProcessingTransaction] = useState(false)

  // Get Ethereum provider with proper typing using type assertion (same as CowSwap)
  const getProvider = useCallback((): EthereumProvider | undefined => {
    if (typeof window !== "undefined" && window.ethereum) {
      // Use type assertion to avoid conflicts with existing declarations
      return window.ethereum as unknown as EthereumProvider
    }
    return undefined
  }, [])

  const navigate = useNavigate()

  useEffect(() => {
    // Prevent browser scroll restoration and ensure page loads at top
    if ("scrollRestoration" in history) {
      history.scrollRestoration = "manual"
    }

    // Set initial position without animation
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
  }, [])

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | null = null

    const fetchPaymentData = async () => {
      try {
        const response = await api.get(`/pay/service/${id}`)
        setPaymentData(response.data)

        // once it's done, stop polling
        if (
          !["pending_user", "pending_anchor", "pending_service", "processing", "refunding"].includes(
            response.data.status,
          )
        ) {
          if (intervalId) {
            clearInterval(intervalId)
            intervalId = null
          }
        }
      } catch (err: any) {
        console.error("Error fetching service payment data:", err)
        setError("Failed to load payment data. Please try again later.")
      } finally {
        setLoading(false)
      }
    }

    // fetch immediately
    fetchPaymentData()

    // then set up polling every 10 seconds
    intervalId = setInterval(fetchPaymentData, 10_000)

    return () => {
      if (intervalId) clearInterval(intervalId)
    }
  }, [id, navigate])

  const handleCopyValue = (value: string, label: string) => {
    navigator.clipboard.writeText(value)
    toast.success(`${label} copiado al portapapeles!`, {
      position: "top-right",
      autoClose: 1000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    })
  }

  // Helper function to parse signature from hex string to r, s, v components
  const parseSignature = (signature: string) => {
    // Remove 0x prefix if present
    const sig = signature.startsWith("0x") ? signature.slice(2) : signature

    // Extract r, s, v components
    const r = "0x" + sig.slice(0, 64)
    const s = "0x" + sig.slice(64, 128)
    const v = Number.parseInt(sig.slice(128, 130), 16)

    return { r, s, v }
  }

  const handleSignHyperCoreTransaction = async () => {
    if (!paymentData?.unsignedTx) {
      toast.error("No hay transacción para firmar")
      return
    }

    // Prevent double submission
    if (isProcessingTransaction) {
      console.log("Transaction already processing, ignoring click")
      return
    }

    console.log("Starting transaction signing process...")
    setIsProcessingTransaction(true)

    try {
      // Use the same provider pattern as CowSwap
      const provider = getProvider()
      if (!provider) {
        toast.error("No se detectó wallet. Por favor instala MetaMask u otro wallet compatible.")
        setIsProcessingTransaction(false)
        return
      }

      console.log("Requesting account access...")
      // Request account access using the provider
      await provider.request({ method: "eth_requestAccounts" })

      // Get the current account using the provider
      const accounts = (await provider.request({ method: "eth_accounts" })) as string[]
      if (accounts.length === 0) {
        toast.error("No hay cuentas conectadas")
        setIsProcessingTransaction(false)
        return
      }

      console.log("Account connected:", accounts[0])
      toast.info("Firmando transacción...", { autoClose: 2000 })

      // Sign the EIP712 typed data (this should be the unsignedTx from your backend)
      console.log("Requesting signature from wallet...")
      const signature = (await provider.request({
        method: "eth_signTypedData_v4",
        params: [accounts[0], JSON.stringify(paymentData.unsignedTx)],
      })) as string

      console.log("Transaction signed successfully")
      toast.info("Enviando transacción a HyperLiquid...", { autoClose: 3000 })

      // Parse signature into r, s, v components
      const parsedSignature = parseSignature(signature)

      // Extract the actual action data from the EIP712 message
      const actionData = paymentData.unsignedTx.message

      console.log("Sending transaction to HyperLiquid API...")
      console.log("Action data:", {
        amount: actionData.amount,
        destination: actionData.destination,
        token: actionData.token,
        nonce: actionData.time,
      })

      // Send directly to HyperLiquid API with correct format
      const hyperLiquidResponse = await fetch("https://api.hyperliquid.xyz/exchange", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: {
            amount: actionData.amount,
            destination: actionData.destination,
            hyperliquidChain: actionData.hyperliquidChain,
            signatureChainId: actionData.signatureChainId,
            time: actionData.time,
            token: actionData.token,
            type: actionData.type,
          },
          isFrontend: true,
          nonce: actionData.time,
          signature: parsedSignature,
          vaultAddress: null,
        }),
      })

      const hyperLiquidResult = await hyperLiquidResponse.json()
      console.log("HyperLiquid Response:", hyperLiquidResult)

      if (hyperLiquidResponse.ok && hyperLiquidResult.status === "ok") {
        console.log("Transaction successfully submitted to HyperLiquid")
        toast.success("¡Transacción enviada a HyperLiquid!")

        // NOW call backend to confirm the transaction (only after successful HyperLiquid submission)
        console.log("Calling backend to confirm transaction...")
        toast.info("Verificando confirmación en blockchain...", { autoClose: false })

        try {
          const confirmResponse = await api.post(`/pay/service/${id}/confirm`, {
            account: accounts[0],
            transactionId: id,
            nonce: actionData.time,
          })

          console.log("Backend confirmation response:", confirmResponse.data)

          // Clear the info toast
          toast.dismiss()

          if (confirmResponse.data.success && confirmResponse.data.found) {
            console.log("Transaction confirmed successfully")
            toast.success("¡Pago confirmado exitosamente! Procesando servicio...")

            // Refresh payment data to show updated status
            const updatedResponse = await api.get(`/pay/service/${id}`)
            setPaymentData(updatedResponse.data)

            // Keep button disabled since payment is now confirmed
          } else if (confirmResponse.data.found === false) {
            console.log("Transaction not yet confirmed in blockchain")
            toast.warning(
              "La transacción fue enviada pero aún no se ha confirmado en blockchain. Seguiremos verificando automáticamente.",
              { autoClose: 5000 },
            )

            // Still refresh to update status
            const updatedResponse = await api.get(`/pay/service/${id}`)
            setPaymentData(updatedResponse.data)

            // Re-enable button in case user needs to retry
            setIsProcessingTransaction(false)
          } else {
            console.error("Confirmation error:", confirmResponse.data.error)
            toast.error(confirmResponse.data.error || "Error al confirmar la transacción")
            setIsProcessingTransaction(false)
          }
        } catch (confirmError: any) {
          toast.dismiss()
          console.error("Error confirming transaction:", confirmError)

          if (confirmError.response?.status === 408) {
            toast.warning(
              "La verificación está tomando más tiempo de lo esperado. Seguiremos verificando automáticamente.",
              { autoClose: 5000 },
            )
          } else {
            toast.error(
              "Error al verificar la transacción: " + (confirmError.response?.data?.error || confirmError.message),
            )
          }

          // Refresh anyway to show any status changes
          try {
            const updatedResponse = await api.get(`/pay/service/${id}`)
            setPaymentData(updatedResponse.data)
          } catch (refreshError) {
            console.error("Error refreshing payment data:", refreshError)
          }

          setIsProcessingTransaction(false)
        }
      } else {
        console.error("HyperLiquid API Error:", hyperLiquidResult)
        toast.error("Error en HyperLiquid: " + (hyperLiquidResult.error || "Error desconocido"))
        setIsProcessingTransaction(false)
      }
    } catch (error: any) {
      console.error("Error in transaction flow:", error)
      if (error.code === 4001) {
        console.log("User rejected transaction")
        toast.error("Transacción cancelada por el usuario")
      } else if (error.name === "TypeError" && error.message.includes("fetch")) {
        toast.error("Error de conexión con HyperLiquid. Verifica tu conexión a internet.")
      } else {
        toast.error("Error al firmar la transacción: " + (error.message || "Error desconocido"))
      }
      setIsProcessingTransaction(false)
    }
  }

  if (loading) {
    return (
      <div className="service-payment-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Cargando datos del pago...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="service-payment-page">
        <div className="error-container">
          <p className="error-message">{error}</p>
        </div>
      </div>
    )
  }

  if (!paymentData) {
    return (
      <div className="service-payment-page">
        <div className="error-container">
          <p>No se pudieron cargar los datos del pago.</p>
        </div>
      </div>
    )
  }

  console.log("Payment Data", paymentData)
  const productMessage = getProductMessage(paymentData)

  const getStatusInfo = (status: string) => {
    switch (status) {
      case "pending_user":
        return { text: "Pendiente...", color: "orange" }
      case "processing":
      case "pending_service":
      case "pending_anchor":
        return { text: "Procesando...", color: "blue" }
      case "refunding":
        return { text: "Reembolsando...", color: "blue" }
      case "complete":
        return { text: "Completada ✅", color: "green" }
      case "refunded":
        return { text: "Reembolsada ↩️", color: "purple" }
      case "error":
        return { text: "Error ❌", color: "red" }
      case "expired":
        return { text: "Expirada", color: "red" }
      default:
        return { text: "Estado Desconocido", color: "gray" }
    }
  }

  const statusInfo = getStatusInfo(paymentData.status)

  return (
    <div className="service-payment-page">
      <h2>Resumen de su Pago</h2>
      <div className="service-payment-card">
        {/* Status Section */}
        <div className="status-section" style={{ color: statusInfo.color }}>
          <div className="status-text">
            <p>{statusInfo.text}</p>
            {paymentData.statusMessage && (
              <span className="info-button" title={paymentData.statusMessage}>
                ℹ️
              </span>
            )}
          </div>

          {paymentData.expiration &&
            ["pending_user", "pending_anchor", "pending_service", "processing", "refunding"].includes(
              paymentData.status,
            ) && (
              <div className="countdown-container">
                <CountdownTimer expiresAt={paymentData.expiration} />
              </div>
            )}
        </div>

        {/* Product Information */}
        <div className="product-section">
          <h3>{productMessage}</h3>

          {/* Gift Card Information - Enhanced Display */}
          {paymentData.productPin && paymentData.status === "complete" && (
            <div className="gift-card-info">
              <h4>Información de Gift Card:</h4>
              {(() => {
                const giftCardInfo = formatGiftCardInfo(paymentData.productPin)
                if (!giftCardInfo) return null

                return (
                  <div className="gift-card-details">
                    {giftCardInfo.hasCard && (
                      <div className="gift-card-row">
                        <span className="label">Tarjeta:</span>
                        <div className="value-with-copy">
                          <span className="gift-card-value">{giftCardInfo.cardNumber}</span>
                          <FiCopy
                            className="copy-icon"
                            onClick={() => handleCopyValue(giftCardInfo.cardNumber!, "Número de tarjeta")}
                          />
                        </div>
                      </div>
                    )}
                    <div className="gift-card-row">
                      <span className="label">PIN:</span>
                      <div className="value-with-copy">
                        <span className="gift-card-value">{giftCardInfo.pin}</span>
                        <FiCopy className="copy-icon" onClick={() => handleCopyValue(giftCardInfo.pin, "PIN")} />
                      </div>
                    </div>
                  </div>
                )
              })()}
            </div>
          )}

          {paymentData.productInfo && <p className="product-info">{paymentData.productInfo}</p>}
        </div>

        {/* Payment Details */}
        <div className="payment-details">
          <h4>Información de Pago</h4>

          {paymentData.email && (
            <p>
              <strong>Email:</strong> {paymentData.email}{" "}
              <FiCopy className="copy-icon" onClick={() => handleCopyValue(paymentData.email ?? "", "Email")} />
            </p>
          )}

          <p>
            <strong>Red:</strong> {paymentData.network}
          </p>

          {paymentData.network === "HyperCore"
            ? paymentData.unsignedTx &&
              paymentData.status === "pending_user" && (
                <div className="hypercore-transaction">
                  <p>
                    <strong>Transacción HyperCore:</strong>
                  </p>
                  <button
                    className="sign-transaction-btn"
                    onClick={handleSignHyperCoreTransaction}
                    disabled={isProcessingTransaction}
                    style={{
                      backgroundColor: isProcessingTransaction ? "#cccccc" : "#67b7d1",
                      color: "white",
                      border: "none",
                      padding: "12px 24px",
                      borderRadius: "8px",
                      cursor: isProcessingTransaction ? "not-allowed" : "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      marginTop: "8px",
                      transition: "background-color 0.2s ease",
                      opacity: isProcessingTransaction ? 0.6 : 1,
                    }}
                    onMouseOver={(e) => {
                      if (!isProcessingTransaction) {
                        const target = e.target as HTMLButtonElement
                        target.style.backgroundColor = "#236a79"
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!isProcessingTransaction) {
                        const target = e.target as HTMLButtonElement
                        target.style.backgroundColor = "#67b7d1"
                      }
                    }}
                  >
                    {isProcessingTransaction ? "Procesando..." : "Firmar y Enviar Transacción"}
                  </button>
                  <p style={{ fontSize: "12px", color: "#666", marginTop: "8px" }}>
                    {isProcessingTransaction
                      ? "Procesando transacción, por favor espera..."
                      : `Haz clic para firmar y enviar la transacción de ${paymentData.onchainAmount} ${paymentData.token.symbol} directamente a HyperLiquid`}
                  </p>
                </div>
              )
            : paymentData.depositAddress && (
                <p>
                  <strong>Transferir a:</strong>{" "}
                  <span className="address-display">{`${paymentData.depositAddress}`}</span>{" "}
                  <FiCopy
                    className="copy-icon"
                    onClick={() => handleCopyValue(paymentData.depositAddress ?? "", "Dirección de depósito")}
                  />
                </p>
              )}

          <p>
            <strong>Monto:</strong> {paymentData.onchainAmount} {paymentData.token.symbol}{" "}
            {paymentData.token.logo_url && (
              <img
                src={paymentData.token.logo_url || "/placeholder.svg"}
                alt={paymentData.token.symbol}
                style={{ width: "20px", marginLeft: "8px" }}
              />
            )}
          </p>

          {paymentData.refundAddress && (
            <p>
              <strong>Dirección de reembolso:</strong>{" "}
              <span className="address-display">{`${paymentData.refundAddress}`}</span>{" "}
              <FiCopy
                className="copy-icon"
                onClick={() => handleCopyValue(paymentData.refundAddress ?? "", "Dirección de reembolso")}
              />
            </p>
          )}
        </div>

        {/* Transaction Hashes */}
        {(paymentData.txHash || paymentData.txHashRefund) && (
          <div className="transaction-section">
            <h4>Transacciones</h4>

            {paymentData.txHash && (
              <p>
                <strong>Hash de transacción:</strong>{" "}
                <span className="transaction-hash">{`${paymentData.txHash}`}</span>{" "}
                <FiCopy
                  className="copy-icon"
                  onClick={() => handleCopyValue(paymentData.txHash!, "Hash de transacción")}
                />
              </p>
            )}

            {paymentData.txHashRefund && paymentData.status === "refunded" && (
              <p>
                <strong>Hash de reembolso: </strong>{" "}
                <span className="transaction-hash refund">{`${paymentData.txHashRefund}`}</span>{" "}
                <FiCopy
                  className="copy-icon"
                  onClick={() => handleCopyValue(paymentData.txHashRefund!, "Hash de reembolso")}
                />
              </p>
            )}
          </div>
        )}
      </div>
      <ToastContainer />
    </div>
  )
}

export default ServicePaymentPage
